import React, { useState, FC } from "react";
import { DELETE_NOTIFICATION } from "../../graphql/mutation";
import { NotificationList } from "@design-system";
import { useNavigate } from "react-router-dom";
import { NotificationMessageModalContainer } from "../notification-modal/notification-message-modal-container";
import {
  NOTIFICATIONS_QUERY,
  useFindNotificationsQuery,
} from "../../graphql/query/notification-query";
import { useMutation } from "../../hooks";
import { NotificationType } from "../../graphql/types";
import {
  DeleteNotification,
  DeleteNotificationVariables,
} from "../../graphql/__generated_types__/DeleteNotification";
import styles from "./notifications-wrapper.module.css";

const { ["notification-wrapper"]: notificationWrapperClassName } = styles;

type NotificationModalState = {
  show: boolean;
  message: string;
};

export const NotificationWrapper: FC = () => {
  const [modalState, setModalState] = useState<NotificationModalState>();

  // Get both types of notifications
  const {
    data: standardData,
    refetch: refetchStandard,
    loading: loadingStandard,
  } = useFindNotificationsQuery(NotificationType.Standard);
  const {
    data: alertData,
    refetch: refetchAlert,
    loading: loadingAlert,
  } = useFindNotificationsQuery(NotificationType.Alert);

  const standardNotifications = standardData?.notifications || [];
  const alertNotifications = alertData?.notifications || [];

  const loading = loadingStandard || loadingAlert;

  // Combine all notifications: Alerts first, then Standard, each sorted by id (most recent first)
  const allNotifications = [...standardNotifications, ...alertNotifications].sort((a, b) => {
    // Alerts come before Standard
    if (a.type !== b.type) {
      return a.type === NotificationType.Alert ? -1 : 1;
    }
    // Within same type, sort by id descending (most recent first)
    return b.id - a.id;
  });

  const [deleteNotification] = useMutation<DeleteNotification, DeleteNotificationVariables>(
    DELETE_NOTIFICATION,
    {
      update: (cache, mutationResult) => {
        const deletedNotificationId = mutationResult?.data?.deleteNotification.notificationId;
        if (!deletedNotificationId) {
          return;
        }

        // Update both caches
        [NotificationType.Standard, NotificationType.Alert].forEach((type) => {
          const data: any = cache.readQuery({ query: NOTIFICATIONS_QUERY, variables: { type } });
          if (data) {
            cache.writeQuery({
              query: NOTIFICATIONS_QUERY,
              data: {
                notifications: data.notifications.filter(
                  (n: any) => n.id !== deletedNotificationId
                ),
              },
              variables: { type },
            });
          }
        });
      },
    }
  );

  const navigate = useNavigate();
  const notificationListProps = {
    loading: loading,
    onReload: () => {
      refetchStandard();
      refetchAlert();
    },
    notifications: allNotifications,
    onViewMessage: (id: number) => {
      setModalState({ show: true, message: allNotifications?.find((x) => x.id === id)?.message! });
    },
    onViewMatter: (notificationId: number, isParent: boolean) => {
      if (isParent) {
        const matterId = allNotifications?.find((n: any) => n.id === notificationId)?.matterId;
        navigate(`/parent/${matterId}`);
        return;
      }
      const matterId = allNotifications?.find((n: any) => n.id === notificationId)?.matterId;
      navigate(`/matter-editor/${matterId}`);
    },
    onDelete: (notificationId: any) => deleteNotification({ variables: { notificationId } }),
    onConfigureNotifications: () => navigate("/configure-notifications"),
  };

  return (
    <div className={notificationWrapperClassName}>
      <NotificationList {...notificationListProps} />
      {modalState?.show && (
        <NotificationMessageModalContainer
          message={modalState.message}
          handleOkClick={() => setModalState({ ...modalState, show: false })}
        />
      )}
    </div>
  );
};
